import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function ParametroGlobalForm() {
    const [form, setForm] = useState({
        clave: '',
        valor: '',
        valorTexto: '',
        descripcion: ''
    });
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const { clave } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    useEffect(() => {
        if (clave) {
            cargarDatos();
        }
    }, [clave]);

    const cargarDatos = async () => {
        try {
            setFetching(true);
            const response = await fetch(`${API_URL}/parametrizacion/parametros-globales/${clave}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setForm({
                    ...data,
                    valor: data.valor !== null ? data.valor : '',
                    valorTexto: data.valorTexto || '',
                    descripcion: data.descripcion || ''
                });
            }
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const method = clave ? 'PUT' : 'POST';
            const url = clave 
                ? `${API_URL}/parametrizacion/parametros-globales/${clave}` 
                : `${API_URL}/parametrizacion/parametros-globales`;
            
            const payload = {
                ...form,
                valor: form.valor === '' ? null : parseFloat(form.valor),
                valorTexto: form.valorTexto || null,
                descripcion: form.descripcion || null
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(clave ? 'Parámetro actualizado' : 'Parámetro creado');
                setTimeout(() => navigate('/parametrizacion/parametros-globales'), 1000);
            } else {
                throw new Error('Error al guardar');
            }
        } catch (error) {
            toast.error('Error al guardar el parámetro');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center h-64">
                <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            <div className="bg-[#1e293b] p-4 flex items-center gap-3 text-white rounded-t-sm shadow-md">
                <button
                    onClick={() => navigate('/parametrizacion/parametros-globales')}
                    className="p-1 hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-white"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-sm font-bold tracking-tight uppercase">
                    {clave ? `Editar Parámetro: ${clave}` : 'Nuevo Parámetro Global'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-amber-400" />
                        <div className="ml-3 text-xs text-amber-800">
                            <strong>Aviso:</strong> Los cambios en parámetros globales pueden afectar inmediatamente a cálculos financieros y validaciones en todo el sistema.
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">Clave Única</label>
                        <input
                            type="text"
                            required
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm rounded-sm font-mono uppercase"
                            value={form.clave}
                            onChange={(e) => setForm({ ...form, clave: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                            placeholder="EJ: LIMITE_MONTO_FIRMA"
                            disabled={!!clave}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">Descripción Detallada</label>
                        <textarea
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm rounded-sm min-h-[80px]"
                            value={form.descripcion}
                            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                            placeholder="Explique el propósito de este parámetro..."
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">Valor Numérico (Opcional)</label>
                        <input
                            type="number"
                            step="any"
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm rounded-sm"
                            value={form.valor}
                            onChange={(e) => setForm({ ...form, valor: e.target.value })}
                            placeholder="Ej: 15000000"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">Valor Texto (Opcional)</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm rounded-sm"
                            value={form.valorTexto}
                            onChange={(e) => setForm({ ...form, valorTexto: e.target.value })}
                            placeholder="Ej: ALTA, ACTIVO, etc."
                        />
                    </div>
                </div>

                <div className="pt-6 flex justify-end gap-3 border-t border-gray-100 mt-8">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/parametros-globales')}
                        className="px-6 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 uppercase transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-8 py-2.5 bg-[#1e293b] hover:bg-slate-800 text-white text-xs font-bold uppercase rounded-sm shadow-lg transition-all disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {clave ? 'Guardar Cambios' : 'Crear Parámetro'}
                    </button>
                </div>
            </form>
        </div>
    );
}
